import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { PROVIDER_ENTITY } from '../../../../constants/entity'
import {
  useAsyncCallback,
  useIsProviderProfileNGAvailable,
  useLinkLikeClick,
  usePageBrowserTabTitle,
  usePageShowReload,
  useSelection,
} from '../../../../hooks'
import { getProviderProfilePathname } from '../../../../utils/link'
import {
  useAsyncTable,
  useTableRefresh,
  useTableReloadAfterMassAction,
} from '../../../Table'

export function useProviderTablePage({
  asyncTableOptions,
  entity = PROVIDER_ENTITY,
  extraSelectionResetDeps = EMPTY_LIST,
  getPermanentFilters,
  isBrowserTabTitleEnabled = true,
  pageTitle,
  sub = null,
  onApplyTableConfig,
}) {
  const [isConfigOpen, setConfigOpen] = useState(false)

  const pageScrollWrapperRef = useRef(null)

  const asyncTable = useAsyncTable({
    ...asyncTableOptions,
    containerRef: pageScrollWrapperRef,
    entity,
    getPermanentFilters,
    sub,
  })

  const {
    columns,
    filterValues,
    handleReload,
    isInitiated,
    isRefetching,
    items,
    view,
  } = asyncTable

  usePageBrowserTabTitle({
    isEnabled: isBrowserTabTitleEnabled,
    isLoading: !isInitiated,
    pageTitle,
  })

  const selection = useSelection({
    data: items,
    pagination: asyncTable.pagination,
  })

  const { handleCleanSelection } = selection

  const {
    handleChangeShouldReloadFilters,
    handleReloadTableAfterMassAction,
    shouldReloadFilters,
  } = useTableReloadAfterMassAction({
    filterValues,
    getPermanentFilters,
    sub,
    onReload: handleReload,
  })

  const { refreshButtonProps } = useTableRefresh({
    isRefetching,
    onReload: handleReload,
  })

  usePageShowReload(handleReload)

  useEffect(() => {
    handleCleanSelection()
  }, [...extraSelectionResetDeps, filterValues])

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  const isProviderProfileNGAvailable = useIsProviderProfileNGAvailable()

  function getProviderProfileInfoLink(id) {
    return getProviderProfilePathname({
      additionalParams: '/info/',
      id,
      isProviderProfileNGAvailable,
    })
  }

  function handleClickRow(id, event) {
    handleClick({ event, link: getProviderProfileInfoLink(id) })
  }

  function handleMouseDownRow(id, event) {
    handleMouseDown({ event, link: getProviderProfileInfoLink(id) })
  }

  const handleApply = useAsyncCallback(
    (obj = EMPTY_OBJECT) => {
      if (!onApplyTableConfig) {
        return Promise.resolve()
      }

      return onApplyTableConfig({
        columns,
        view,
        ...obj,
      })
    },
    [columns, onApplyTableConfig, view]
  )

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  return {
    ...asyncTable,
    ...selection,
    entity,
    handleApply,
    handleChangeShouldReloadFilters,
    handleClickRow,
    handleConfigOpen,
    handleMouseDownRow,
    handleReloadTableAfterMassAction,
    isConfigOpen,
    pageScrollWrapperRef,
    refreshButtonProps,
    setConfigOpen,
    shouldReloadFilters,
  }
}
